import type { Locale, OtpChallengeResponse, PinSessionResponse, UserProfile } from '@ccex/shared';
import { effectiveTimezone as resolveTimezone } from '@ccex/shared';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api } from '@/api/client';
import { ApiError } from '@/api/http';
import { reportUser } from '@/monitoring/report';
import { detectDeviceTimezone } from '@/utils/timezones';

/** Token lives in memory only: reopening the Mini App yields fresh initData, so there is nothing to persist. */
export const useSessionStore = defineStore('session', () => {
  const token = ref<string | null>(null);
  const profile = ref<UserProfile | null>(null);
  const rawInitData = ref<string>('');
  /** Email the invitation link was issued for; in memory only, like the token. Null when the app was not opened from an invitation link. */
  const inviteEmail = ref<string | null>(null);
  /** Invite token from the wallet button URL `?invite=<token>` (set by the bot's /start reply); memory only, sent with login. */
  const inviteToken = ref<string | null>(null);
  /** Support bot username from the login response (empty when getMe failed); kept from a USER_BLOCKED refusal too. */
  const botUsername = ref('');
  /**
   * True from login until the PIN is verified (spec 2026-10-30 §6.1). Login never issues a
   * verified token, so a profile with a PIN always starts locked; a 423 from any call locks again.
   */
  const pinLocked = ref(false);
  /** Background pause after which usePinLock re-locks; from the login response. */
  const resumeLockSeconds = ref(60);
  const isAuthenticated = computed(() => token.value !== null && profile.value !== null);
  /** Zone every date in the app is rendered in; UTC until a profile is loaded. */
  const effectiveTimezone = computed(() =>
    profile.value ? resolveTimezone(profile.value) : 'UTC',
  );

  function setRawInitData(value: string): void {
    rawInitData.value = value;
  }

  function setInviteToken(value: string | null): void {
    inviteToken.value = value;
  }

  let pendingLogin: Promise<UserProfile> | null = null;

  /** Concurrent 401s must not fire two logins; the second caller awaits the first's in-flight request. */
  async function login(initData: string = rawInitData.value): Promise<UserProfile> {
    rawInitData.value = initData;
    // No init data means the Telegram host never handed us any: the app was opened outside
    // Telegram, or VITE_TG_MOCK_INIT_DATA was empty in dev. Posting '' would only earn a
    // VALIDATION_FAILED the catalogue has no message for, which surfaces as the generic
    // "something went wrong"; fail here with the code that actually tells the user what to do.
    if (!initData) {
      throw new ApiError('AUTH_INVALID_INIT_DATA', 0, 'Missing Telegram init data');
    }
    if (!pendingLogin) {
      pendingLogin = (async () => {
        let response;
        try {
          response = await api.loginWithInitData(
            initData,
            detectDeviceTimezone(),
            inviteToken.value,
          );
        } catch (error) {
          // A blocked user never gets a session, but the refusal names the bot so the blocked
          // screen can still point at support (spec 2026-10-28 §7).
          if (
            error instanceof ApiError &&
            error.code === 'USER_BLOCKED' &&
            typeof error.extra.botUsername === 'string'
          ) {
            botUsername.value = error.extra.botUsername;
          }
          throw error;
        }
        token.value = response.accessToken;
        profile.value = response.user;
        inviteEmail.value = response.inviteEmail ?? null;
        botUsername.value = response.botUsername;
        pinLocked.value = response.user.pinEnabled;
        resumeLockSeconds.value = response.pinResumeLockSeconds;
        // The internal UUID only: enough for support to find the user, nothing else (spec §3.5).
        reportUser(response.user.id);
        return response.user;
      })().finally(() => {
        pendingLogin = null;
      });
    }
    return pendingLogin;
  }

  /** Runs an authenticated call; on 401 logs in again once and retries. */
  async function withAuth<T>(call: (token: string) => Promise<T>): Promise<T> {
    try {
      return await call(token.value ?? '');
    } catch (error) {
      if (error instanceof ApiError && error.status === 423 && error.code === 'PIN_REQUIRED') {
        pinLocked.value = true;
        throw error;
      }
      // The PIN was turned off or on from another device: our idea of the PIN state is stale.
      // Re-login recomputes `pinLocked` and the profile; the caller still gets the original error.
      if (
        error instanceof ApiError &&
        error.status === 409 &&
        (error.code === 'PIN_NOT_SET' || error.code === 'PIN_ALREADY_SET')
      ) {
        await login().catch(() => undefined);
        throw error;
      }
      if (error instanceof ApiError && error.status === 401 && rawInitData.value) {
        await login();
        return call(token.value ?? '');
      }
      throw error;
    }
  }

  async function setLocale(locale: Locale): Promise<void> {
    profile.value = await withAuth((t) => api.setLocale(t, locale));
  }

  async function setTimezone(timezone: string | null): Promise<void> {
    profile.value = await withAuth((t) => api.setTimezone(t, timezone));
  }

  /** Every PIN endpoint answers with a fresh token and the profile; `pinLocked` follows the response. */
  function applyPinSession(response: PinSessionResponse): void {
    token.value = response.accessToken;
    profile.value = response.user;
    pinLocked.value = false;
  }

  async function enablePin(pin: string): Promise<void> {
    applyPinSession(await withAuth((t) => api.enablePin(t, pin)));
  }

  async function changePin(currentPin: string, pin: string): Promise<void> {
    applyPinSession(await withAuth((t) => api.changePin(t, currentPin, pin)));
  }

  async function disablePin(currentPin: string): Promise<void> {
    applyPinSession(await withAuth((t) => api.disablePin(t, currentPin)));
  }

  async function verifyPin(pin: string): Promise<void> {
    applyPinSession(await withAuth((t) => api.verifyPin(t, pin)));
  }

  function requestPinReset(): Promise<OtpChallengeResponse> {
    return withAuth((t) => api.requestPinReset(t));
  }

  async function confirmPinReset(code: string): Promise<void> {
    applyPinSession(await withAuth((t) => api.confirmPinReset(t, code)));
  }

  /**
   * Back from the background after the pause (spec 2026-10-30 §6.3): drop the verified token so
   * the next call re-logs in and gets an unverified one. Client and server agree again instead of
   * the client merely pretending to be locked.
   */
  function relock(): void {
    if (!profile.value?.pinEnabled) return;
    pinLocked.value = true;
    token.value = null;
  }

  function updateProfile(next: UserProfile): void {
    profile.value = next;
  }

  return {
    token,
    profile,
    rawInitData,
    inviteEmail,
    botUsername,
    inviteToken,
    isAuthenticated,
    effectiveTimezone,
    setRawInitData,
    setInviteToken,
    login,
    withAuth,
    setLocale,
    setTimezone,
    updateProfile,
    pinLocked,
    resumeLockSeconds,
    enablePin,
    changePin,
    disablePin,
    verifyPin,
    requestPinReset,
    confirmPinReset,
    relock,
  };
});
